// Turns the (permission-filtered) Vuexy menu config into NTTS nav sections.
//  - A top-level item with children -> collapsible sidebar group
//  - A top-level item with a navLink -> plain sidebar link
//  - Children with a `group` field are listed under that sub-heading
//  - Roles & access is pinned to the bottom of the sidebar

const PINNED_BOTTOM = ['roles-permissions']

const pathOf = (navLink) => String(navLink || '').split('?')[0]

// [{ title: 'People', links: [...] }, ...] in first-appearance order;
// links without a group share one untitled block
const subgroupsOf = (links) => {
  const groups = []
  links.forEach((link) => {
    const title = link.group || null
    let group = groups.find((g) => g.title === title)
    if (!group) {
      group = { title, links: [] }
      groups.push(group)
    }
    group.links.push(link)
  })
  return groups
}

export const buildSections = (menuData = []) => {
  const directLinks = menuData.filter((item) => item.navLink && !item.children)
  const groups = menuData.filter((item) => item.children?.length)

  // One single-page section per direct link, so the panel never lists
  // unrelated top-level pages together
  const directSections = directLinks.map((link) => ({
    id: `link:${link.navLink}`,
    title: link.title,
    links: [link],
    direct: true
  }))

  const sections = groups.map((group) => {
    const links = group.children.filter((child) => child.navLink)
    return {
      id: group.id,
      title: group.title,
      icon: group.icon,
      links,
      subgroups: subgroupsOf(links),
      pinBottom: PINNED_BOTTOM.includes(group.id)
    }
  })

  return {
    directSections,
    sections: [
      ...sections.filter((s) => !s.pinBottom),
      ...sections.filter((s) => s.pinBottom)
    ],
    railLinks: directLinks
  }
}

// Longest navLink that prefixes the current path wins, so detail pages
// (e.g. /apps/master/user/details) still highlight their parent link.
export const findActive = (pathname, { directSections, sections }) => {
  let best = null
  const consider = (section, link) => {
    const path = pathOf(link.navLink)
    if (pathname === path || pathname.startsWith(`${path}/`)) {
      if (!best || path.length > pathOf(best.link.navLink).length) {
        best = { section, link }
      }
    }
  }
  ;[...directSections, ...sections].forEach((section) =>
    section.links.forEach((link) => consider(section, link))
  )
  return best
}

// "ADMIN" -> "Administrator", "MENTOR" -> "Mentor"
const ROLE_LABELS = { ADMIN: 'Administrator' }

export const formatRoles = (roles) =>
  (Array.isArray(roles) ? roles : roles ? [roles] : [])
    .map((role) => {
      const key = String(role).toUpperCase()
      if (ROLE_LABELS[key]) return ROLE_LABELS[key]
      const words = String(role).replace(/[_-]+/g, ' ').toLowerCase()
      return words.charAt(0).toUpperCase() + words.slice(1)
    })
    .join(', ')

export const initialsOf = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || '?'
